import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { FileCard } from '@/components/FileCard';
import { useProducts } from '@/hooks/useProducts';
import { useCategories } from '@/hooks/useCategories';
import { MapPin, Hash, Globe, BarChart3, ArrowRight, Loader2, FileSpreadsheet } from 'lucide-react';
import { SEO, getBreadcrumbSchema } from '@/hooks/useSEO';
import { getTranslatedField } from '@/lib/translationHelpers';
import { CountryFilter } from '@/components/CountryFilter';
import { SortSelect } from '@/components/SortSelect';
import { sortProducts, type SortBy } from '@/lib/sortProducts';

const categoryIcons: Record<string, React.ElementType> = {
  addresses: MapPin,
  postalCodes: Hash,
  geodata: Globe,
  statistics: BarChart3,
};

export default function DataFilesPage() {
  const { t } = useTranslation();
  const { lang = 'sv' } = useParams<{ lang: string }>();
  const [searchParams] = useSearchParams();
  const categoryFromUrl = searchParams.get('category');

  const { data: categories = [], isLoading: loadingCategories } = useCategories();
  const { data: products = [], isLoading: loadingProducts } = useProducts();

  // Set active category: from URL param, or first category from DB
  const defaultCategory = categoryFromUrl
    && categories.some((c) => c.slug === categoryFromUrl)
    ? categoryFromUrl
    : categories[0]?.slug || '';

  const [activeCategory, setActiveCategory] = useState<string>(defaultCategory);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<SortBy>('sku');

  // Update active category when categories load and current is empty
  const resolvedCategory = activeCategory || categories[0]?.slug || '';

  const isLoading = loadingCategories || loadingProducts;

  // SEO must render on first pass so its tags are picked up by the SSG
  // renderer; otherwise the early-return below would mean the prerendered
  // HTML for /datafiler ships with the index.html-fallback meta only.
  const seo = (
    <SEO
      title={t('dataFiles.title')}
      description={t('meta.dataFiles')}
      path="/datafiler"
      jsonLd={getBreadcrumbSchema([
        { name: t('common.home'), url: `https://postnummerservice.se/${lang}` },
        { name: t('dataFiles.title'), url: `https://postnummerservice.se/${lang}/datafiler` },
      ])}
    />
  );

  if (isLoading) {
    return (
      <div className="container py-12 lg:py-16 flex items-center justify-center">
        {seo}
        <Loader2 className="w-8 h-8 animate-spin text-secondary" />
      </div>
    );
  }

  return (
    <div className="container py-12 lg:py-16">
      {seo}
      {/* Hero Section */}
      <div className="max-w-3xl mb-12">
        <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
          {t('dataFiles.title')}
        </h1>
        <p className="text-lg text-muted-foreground leading-relaxed">
          {t('dataFiles.subtitle')}
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3 mb-10">
        {/* Category filter */}
        {categories.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground mr-1">
              {t('filter.byCategory')}
            </span>
            {categories.map((category) => {
              const Icon = categoryIcons[category.slug] || FileSpreadsheet;
              const isActive = resolvedCategory === category.slug;
              return (
                <button
                  key={category.id}
                  type="button"
                  onClick={() => setActiveCategory(category.slug)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-card border-border hover:bg-muted text-foreground'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {getTranslatedField(category, 'name', lang)}
                </button>
              );
            })}
          </div>
        )}

        {/* Country filter + Sort */}
        <div className="flex flex-wrap items-center gap-3">
          <CountryFilter
            selected={selectedCountries}
            onChange={setSelectedCountries}
            showTooltip
          />
          <div className="ml-auto">
            <SortSelect value={sortBy} onChange={setSortBy} />
          </div>
        </div>
      </div>

      {/* Category Content */}
      {categories.length > 0 && (
        <Tabs
          value={resolvedCategory}
          onValueChange={setActiveCategory}
          className="mb-12"
        >

          {categories.map((category) => {
            const categoryProducts = sortProducts(
              products
                .filter((p) => p.ws_category === category.slug)
                .filter((p) =>
                  selectedCountries.length === 0 ||
                  p.available_countries?.some((c) => selectedCountries.includes(c))
                ),
              sortBy
            );
            return (
              <TabsContent key={category.id} value={category.slug} className="mt-0">
                {categoryProducts.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">
                    {t('dataFiles.noProducts')}
                  </p>
                ) : (
                  <div className="space-y-4">
                    {categoryProducts.map((product) => (
                      <FileCard key={product.id} product={product} />
                    ))}
                  </div>
                )}
              </TabsContent>
            );
          })}
        </Tabs>
      )}

      {/* CTA Section */}
      <section className="text-center bg-primary/5 rounded-2xl p-8 lg:p-12">
        <h2 className="text-2xl font-semibold mb-4">{t('dataFiles.cta.title')}</h2>
        <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
          {t('dataFiles.cta.description')}
        </p>
        <Button asChild size="lg">
          <Link to={`/${lang}/shop`}>
            {t('dataFiles.cta.button')}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Link>
        </Button>
      </section>
    </div>
  );
}
